import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Activity,
  AlertTriangle,
  Clock,
  Flame,
  Plus,
  AlertCircle,
  Trash2,
  Video,
  GraduationCap,
  Link2,
  MessageCirclePlus,
  MessageCircle,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  Layers,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import { RENDEZ_VOUS_KIND_LABELS, SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SchoolLevel } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import {
  useTutorStudentDetail,
  useMyExercises,
  useStudentExerciseProgress,
  useTutorStudents,
} from '@/lib/tutor/queries';
import { useRevokeStudent, useInviteStudentById } from '@/lib/tutor/mutations';
import { useMyArchivedRendezVousIds } from '@/lib/rendezvous-archive';
import { buildAgenda, dayLabel, timeLabel } from '@/lib/tutor/dashboard-overview';
import { isOverdue, sortActiveAssignments, studentAlerts } from '@/lib/tutor/student-fiche';
import { CollapsiblePanel } from '@/components/collapsible-panel';
import { useNow } from '@/hooks/use-now';
import { useAssignExercise, useDeleteAssignment } from '@/lib/tutor/assignment-mutations';
import { useOffersAcademicMentoring, useSetOffersAcademicMentoring } from '@/lib/tutor/rendezvous';
import { useAlternantSwitch } from '@/lib/tutor/student-alternant';
import { useTutorBookingSlots, useTutorPastBookingsForStudent } from '@/lib/tutor/booking';
import { useCreateTutorInviteLink, buildInviteUrl, translateInviteError } from '@/lib/tutor-invite';
import { copyText } from '@/lib/clipboard';
import { InviteLinkField } from '@/components/invite-link-field';
import { StudentProgressBlock } from '@/components/tutor/student-progress-block';
import { PastRendezVousCard } from '@/components/past-rendezvous-card';
import { DocumentsPanel } from '@/components/documents/documents-panel';
import { DiscussionsList } from '@/features/discussions/discussions-list';
import { TutorStudentSkillcards } from '@/features/skillcards/tutor-student-skillcards';
import { TutorStudentEvaluations } from '@/features/evaluations/tutor-student-evaluations';
import { useCreateDiscussion, useDiscussions } from '@/lib/discussions/queries';
import { DiscussionError } from '@/lib/discussions/types';
import { ExercisePickerField, toExercisePickerItem } from '@/components/pickers/exercise-picker';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const ATTEMPT_META = {
  completed: { icon: CheckCircle2, color: 'text-xp-500', bg: 'bg-xp-50', label: 'Terminé' },
  in_progress: {
    icon: Activity,
    color: 'text-primary-600',
    bg: 'bg-primary-50',
    label: 'En cours',
  },
  abandoned: {
    icon: AlertTriangle,
    color: 'text-accent-500',
    bg: 'bg-accent-50',
    label: 'Abandonné',
  },
} as const;

const ASSIGN_STATUS_META = {
  pending: { icon: Clock, color: 'text-level-600', bg: 'bg-level-50', label: 'À faire' },
  in_progress: {
    icon: Activity,
    color: 'text-primary-600',
    bg: 'bg-primary-50',
    label: 'En cours',
  },
} as const;

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `il y a ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-ink-500 mb-3 text-[11px] font-bold uppercase tracking-[.1em]">
      {children}
    </h2>
  );
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-5 text-center">
      <p className="text-ink-400 font-sans text-sm">{children}</p>
    </div>
  );
}

/**
 * Mentorat académique (alternance, migration 0237) — relation SÉPARÉE de
 * tutor_links. Deux façons de rattacher un alternant à ce tuteur : (1) l'élève
 * le choisit lui-même (lien d'invitation « étudiant alternance », migration
 * 0241) ; (2) le tuteur le rattache directement dans sa relation avec lui
 * (`tutor_set_student_alternant`, migration 0250 — exige un tutor_links actif).
 * `declared` = l'étudiant s'est déclaré alternant lui-même (le retrait ne lui
 * enlève alors que ce tuteur, pas son statut).
 */
function AcademicMentorshipCard({
  tutorId,
  studentId,
  studentName,
  isMentee,
  declared,
}: {
  tutorId: string;
  studentId: string;
  studentName: string;
  isMentee: boolean;
  declared: boolean;
}) {
  const { data: offersMentoring, isLoading: mentoringLoading } =
    useOffersAcademicMentoring(tutorId);
  const setOffersMentoring = useSetOffersAcademicMentoring(tutorId);
  const createLink = useCreateTutorInviteLink();
  const switcher = useAlternantSwitch(tutorId);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  if (isMentee) {
    return (
      <div className="border-primary-100 bg-primary-50 mb-6 space-y-3 rounded-2xl border p-4">
        <div className="flex items-center gap-3">
          <GraduationCap className="text-primary-600 h-5 w-5 shrink-0" />
          <p className="text-primary-700 font-sans text-sm">
            Tu es le tuteur académique de {studentName} — entretiens gratuits disponibles.
          </p>
        </div>
        <Link
          to="/tutor/rendez-vous/new"
          search={{ kind: 'entretien', studentId }}
          className="border-primary-200 text-primary-700 hover:bg-primary-100 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border bg-white px-5 text-sm font-bold transition-all active:scale-[.98]"
        >
          <Video className="h-4 w-4" />
          Programmer un entretien académique
        </Link>
        <button
          type="button"
          onClick={() => void switcher.backToStudent({ studentId, name: studentName, declared })}
          disabled={switcher.isPending}
          className="text-primary-700 flex min-h-11 w-full items-center justify-center text-xs font-medium underline disabled:opacity-50"
        >
          {declared ? 'Retirer de mes étudiants alternants' : 'Repasser en élève'}
        </button>
        {switcher.error && (
          <p role="alert" className="text-error-500 text-xs">
            {switcher.error}
          </p>
        )}
      </div>
    );
  }

  if (mentoringLoading) return null;

  if (!offersMentoring) {
    return (
      <div className="border-ink-100 mb-6 rounded-2xl border bg-white p-4">
        <p className="text-ink-700 font-sans text-sm font-semibold">
          {studentName} est en alternance
        </p>
        <p className="text-ink-500 mt-1 font-sans text-xs">
          Active le mentorat académique pour pouvoir devenir son tuteur académique et lui proposer
          des entretiens gratuits.
        </p>
        <Button
          className="mt-3 w-full"
          onClick={() => setOffersMentoring.mutate(true)}
          disabled={setOffersMentoring.isPending}
        >
          Activer le mentorat académique
        </Button>
      </div>
    );
  }

  // Génère le lien puis L'AFFICHE (champ copiable) : la copie automatique après l'appel
  // réseau est souvent refusée (geste utilisateur perdu) et une erreur ne doit jamais
  // être avalée en silence.
  const handleGenerateLink = async () => {
    setLinkError(null);
    try {
      const res = await createLink.mutateAsync({ tutorId, kind: 'academic', subjects: [] });
      const url = buildInviteUrl(window.location.origin, res.token);
      setInviteUrl(url);
      void copyText(url); // bonus silencieux — le champ ci-dessous reste la voie fiable
    } catch (err) {
      setLinkError(translateInviteError(err instanceof Error ? err.message : ''));
    }
  };

  return (
    <div className="border-ink-100 mb-6 rounded-2xl border bg-white p-4">
      <p className="text-ink-700 font-sans text-sm font-semibold">
        {studentName} est en alternance
      </p>
      <p className="text-ink-500 mt-1 font-sans text-xs">
        Il/elle ne t'a pas encore choisi comme tuteur académique. Rattache-le directement à toi, ou
        partage-lui ton lien d'invitation, pour débloquer les entretiens gratuits.
      </p>
      <Button
        className="mt-3 w-full"
        onClick={() => void switcher.toAlternant({ studentId, name: studentName })}
        disabled={switcher.isPending}
      >
        <GraduationCap className="h-4 w-4" />
        Le rattacher comme étudiant alternant
      </Button>
      <Button
        variant="outline"
        className="mt-2 w-full"
        onClick={() => void handleGenerateLink()}
        disabled={createLink.isPending}
      >
        <Link2 className="h-4 w-4" />
        {inviteUrl ? "Régénérer le lien d'invitation" : "Générer le lien d'invitation académique"}
      </Button>
      {inviteUrl && (
        <div className="mt-2">
          <InviteLinkField url={inviteUrl} shareTitle="Deviens mon étudiant en alternance" />
        </div>
      )}
      {linkError && (
        <p role="alert" className="text-error-500 mt-2 text-xs">
          {linkError}
        </p>
      )}
      {switcher.error && (
        <p role="alert" className="text-error-500 mt-2 text-xs">
          {switcher.error}
        </p>
      )}
    </div>
  );
}

/**
 * Élève ordinaire (non alternant) : le tuteur peut le passer en étudiant
 * alternant dans sa relation avec lui (migration 0250) — discret, car la plupart
 * des élèves ne sont pas concernés.
 */
function BecomeAlternantAction({
  tutorId,
  studentId,
  studentName,
}: {
  tutorId: string;
  studentId: string;
  studentName: string;
}) {
  const switcher = useAlternantSwitch(tutorId);
  return (
    <div className="-mt-2 mb-6">
      <button
        type="button"
        onClick={() => void switcher.toAlternant({ studentId, name: studentName })}
        disabled={switcher.isPending}
        className="text-primary-600 inline-flex min-h-11 items-center gap-1.5 text-xs font-medium underline disabled:opacity-50"
      >
        <GraduationCap className="h-3.5 w-3.5" />
        Passer en étudiant alternant
      </button>
      {switcher.error && (
        <p role="alert" className="text-error-500 text-xs">
          {switcher.error}
        </p>
      )}
    </div>
  );
}

export function TutorStudentDetailPage() {
  const { studentId } = useParams({ strict: false }) as { studentId: string };
  const { user } = useAuth();
  const tutorId = user?.id ?? '';
  const navigate = useNavigate();
  const nowMs = useNow(60_000);

  const { data, isLoading } = useTutorStudentDetail(studentId, tutorId);
  const discussions = useDiscussions();
  const studentDiscussions = (discussions.data ?? []).filter((d) => d.studentId === studentId);
  const createDiscussion = useCreateDiscussion();
  const [discussionError, setDiscussionError] = useState<string | null>(null);
  const { data: exerciseProgress = [] } = useStudentExerciseProgress(studentId, tutorId);
  const { data: exercises } = useMyExercises(tutorId);
  const { data: pastBookings = [] } = useTutorPastBookingsForStudent(tutorId, studentId);
  const { data: slots = [] } = useTutorBookingSlots(tutorId);
  const { data: archivedIds } = useMyArchivedRendezVousIds(tutorId);
  const { data: linkedStudents = [] } = useTutorStudents(tutorId);
  const assignExercise = useAssignExercise();
  const deleteAssignment = useDeleteAssignment();
  const revokeStudent = useRevokeStudent();
  const inviteStudent = useInviteStudentById();

  const upcoming = useMemo(
    () =>
      buildAgenda(slots, nowMs, archivedIds, 365)
        .days.flatMap((d) => d.items)
        .filter((i) => i.studentId === studentId),
    [slots, nowMs, archivedIds, studentId],
  );

  // Contact déjà connu (CET élève) : aucune sélection, aucun titre à saisir —
  // l'IA le déduira du contenu dès le 1er message (discussion-subject.ts).
  async function handleNewDiscussion() {
    setDiscussionError(null);
    try {
      const discussion = await createDiscussion.mutateAsync({
        tutorId,
        studentId,
        subject: { scope: 'free' },
      });
      void navigate({ to: '/discussions/$discussionId', params: { discussionId: discussion.id } });
    } catch (err) {
      setDiscussionError(
        err instanceof DiscussionError ? err.message : "Impossible d'ouvrir la discussion.",
      );
    }
  }

  /** « Écrire » : reprend la discussion la plus récente, sinon en ouvre une. */
  function handleWrite() {
    const latest = studentDiscussions[0];
    if (latest) {
      void navigate({ to: '/discussions/$discussionId', params: { discussionId: latest.id } });
      return;
    }
    void handleNewDiscussion();
  }

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignExerciseId, setAssignExerciseId] = useState('');
  const [assignDueAt, setAssignDueAt] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [assignSuccess, setAssignSuccess] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const publishedExercises = (exercises ?? []).filter((e) => e.status === 'published');

  function openAssign() {
    setAssignOpen(true);
    document.getElementById('devoirs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function handleAssign(ev: React.FormEvent) {
    ev.preventDefault();
    if (!assignExerciseId) return;
    setAssignError(null);
    try {
      await assignExercise.mutateAsync({
        studentId,
        exerciseId: assignExerciseId,
        dueAt: assignDueAt || null,
        note: assignNote || null,
      });
      setAssignExerciseId('');
      setAssignDueAt('');
      setAssignNote('');
      setAssignSuccess(true);
      setAssignOpen(false);
      setTimeout(() => setAssignSuccess(false), 3000);
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : "Erreur lors de l'assignation");
    }
  }

  const link = linkedStudents.find((s) => s.student_id === studentId);
  async function handleRevoke() {
    if (!link || !data) return;
    if (
      !window.confirm(
        `Retirer ${data.display_name} de tes élèves ? Tu ne verras plus sa progression ; ses fichiers et discussions restent consultables.`,
      )
    )
      return;
    await revokeStudent.mutateAsync({ tutorId, linkId: link.link_id });
    void navigate({ to: '/tutor/contacts' });
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 sm:py-8">
        <div className="bg-ink-100 mb-6 h-5 w-24 animate-pulse rounded-lg" />
        <div className="bg-ink-100 mb-4 h-32 animate-pulse rounded-2xl" />
        <div className="bg-ink-100 h-48 animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 sm:py-8">
        <BackLink />
        <p className="text-ink-500 font-sans text-sm">Élève introuvable ou non accessible.</p>
      </div>
    );
  }

  const isActive = data.link_status === 'active';
  const isPending = data.link_status === 'pending';
  const initials = data.display_name
    .split(' ')
    .map((n: string) => n[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
  const isRecent = data.last_activity_at
    ? nowMs - new Date(data.last_activity_at).getTime() < 7 * 24 * 60 * 60 * 1000
    : false;
  const activeAssignments = sortActiveAssignments(data.active_assignments, nowMs);
  const overdueCount = activeAssignments.filter((a) => isOverdue(a, nowMs)).length;
  const alerts = isActive
    ? studentAlerts({
        lastActivityAt: data.last_activity_at,
        overdue: overdueCount,
        nextRendezVous: upcoming[0] ?? null,
        nowMs,
      })
    : [];
  const firstName = data.display_name.split(' ')[0] || data.display_name;

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      <BackLink />

      {/* ── En-tête : qui, et ce qui mérite l'attention ─────────────────────── */}
      <div className="border-ink-200 ocean:bg-white/5 mb-4 rounded-2xl border bg-white p-5">
        <div className="flex items-center gap-4">
          {data.avatar_url ? (
            <img
              src={data.avatar_url}
              alt=""
              className="h-14 w-14 shrink-0 rounded-2xl object-cover"
            />
          ) : (
            <div className="bg-primary-100 text-primary-700 font-display flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl font-extrabold">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-ink-900 truncate text-xl font-extrabold">
              {data.display_name}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="text-ink-500 font-sans text-sm">
                {data.level
                  ? (LEVEL_LABEL[data.level as SchoolLevel] ?? data.level)
                  : 'Niveau inconnu'}
              </span>
              {isPending ? (
                <span className="bg-level-50 text-level-600 font-display inline-flex h-6 items-center gap-1 rounded-full px-2 text-xs font-bold">
                  <Clock className="h-3 w-3" /> En attente
                </span>
              ) : isActive && isRecent ? (
                <span className="bg-xp-50 text-xp-600 font-display inline-flex h-6 items-center rounded-full px-2 text-xs font-bold">
                  Actif
                </span>
              ) : isActive ? (
                <span className="bg-ink-100 text-ink-500 font-display inline-flex h-6 items-center rounded-full px-2 text-xs font-bold">
                  Inactif
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {isActive && (
          <>
            <div className="border-ink-100 text-ink-500 mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 font-sans text-sm">
              <span>
                <span className="text-xp-600 font-mono font-bold">{data.xp}</span> XP
              </span>
              <span className="flex items-center gap-1">
                <Flame className="text-accent-400 h-3.5 w-3.5" aria-hidden />
                Série {data.streak_days} j
              </span>
              {data.last_activity_at && <span>Vu {relativeTime(data.last_activity_at)}</span>}
            </div>
            <ul className="mt-3 space-y-1.5" aria-label="À surveiller">
              {alerts.map((a) => (
                <li
                  key={a.text}
                  className={cn(
                    'flex items-start gap-2 font-sans text-sm',
                    a.tone === 'danger' && 'text-error-600',
                    a.tone === 'warning' && 'text-amber-700',
                    a.tone === 'info' && 'text-primary-700',
                    a.tone === 'ok' && 'text-xp-600',
                  )}
                >
                  {a.tone === 'info' ? (
                    <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  ) : a.tone === 'ok' ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  )}
                  {a.text}
                </li>
              ))}
            </ul>
          </>
        )}

        {isPending && data.invited_at && (
          <p className="text-ink-500 mt-3 font-sans text-sm">
            Invitation envoyée le{' '}
            {new Date(data.invited_at).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}{' '}
            — en attente d'acceptation.
          </p>
        )}
      </div>

      {/* ── Actions : les 4 gestes du prof avec cet élève ────────────────────── */}
      {isActive && (
        <div className="mb-6 space-y-2">
          <Link
            to="/tutor/rendez-vous/new"
            search={{ kind: 'express', studentId }}
            className="bg-primary-500 font-display hover:bg-primary-600 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-sm transition-all active:scale-[.98]"
          >
            <Video className="h-4 w-4" />
            Nouveau rendez-vous
          </Link>
          <div className="grid grid-cols-3 gap-2">
            <ActionButton icon={MessageCircle} label="Écrire" onClick={handleWrite} />
            <ActionButton icon={ClipboardList} label="Devoir" onClick={openAssign} />
            <ActionButton
              icon={Layers}
              label="SKILLCARD"
              onClick={() =>
                document
                  .getElementById('skillcards')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            />
          </div>
          {discussionError && !studentDiscussions.length && (
            <p role="alert" className="text-accent-700 text-xs font-medium">
              {discussionError}
            </p>
          )}
        </div>
      )}

      {/* ── Mentorat académique (alternance) ───────────────────────────────────── */}
      {isActive && data.is_alternant && (
        <AcademicMentorshipCard
          tutorId={tutorId}
          studentId={studentId}
          studentName={data.display_name}
          isMentee={data.academic_tutor_id === tutorId}
          declared={data.is_higher_ed_student}
        />
      )}

      {/* ── Devoirs : en retard d'abord, ajout replié ──────────────────────────── */}
      {isActive && (
        <section id="devoirs" className="mb-6 scroll-mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SectionTitle>
              Devoirs en cours
              {activeAssignments.length > 0 && ` (${activeAssignments.length})`}
            </SectionTitle>
            {!assignOpen && (
              <button
                type="button"
                onClick={() => setAssignOpen(true)}
                className="text-primary-600 hover:bg-primary-50 -mt-3 flex min-h-[2.75rem] items-center gap-1.5 rounded-xl px-2 text-sm font-semibold active:scale-95"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Assigner
              </button>
            )}
          </div>
          {assignSuccess && (
            <div className="text-xp-700 bg-xp-50 border-xp-100 mb-3 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Exercice assigné avec succès !
            </div>
          )}
          {assignOpen && (
            <form
              onSubmit={handleAssign}
              aria-label="Assigner un exercice"
              className="border-primary-200 ocean:bg-white/5 mb-3 space-y-3 rounded-2xl border bg-white p-5"
            >
              <ExercisePickerField
                label="Exercice *"
                items={publishedExercises.map(toExercisePickerItem)}
                value={assignExerciseId || null}
                onChange={setAssignExerciseId}
                title="Choisir l’exercice à assigner"
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="font-display text-ink-700 mb-1.5 block text-xs font-semibold">
                    Date limite <span className="text-ink-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    type="date"
                    value={assignDueAt}
                    onChange={(e) => setAssignDueAt(e.target.value)}
                    className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-400 h-11 w-full rounded-xl border px-3 font-sans text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-display text-ink-700 mb-1.5 block text-xs font-semibold">
                    Message <span className="text-ink-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    type="text"
                    value={assignNote}
                    onChange={(e) => setAssignNote(e.target.value)}
                    maxLength={200}
                    placeholder={`Pour ${firstName}…`}
                    className="border-ink-200 bg-ink-50 text-ink-900 placeholder:text-ink-400 focus:border-primary-400 h-11 w-full rounded-xl border px-3 font-sans text-sm focus:outline-none"
                  />
                </div>
              </div>
              {assignError && (
                <div className="text-error-700 bg-error-50 border-error-100 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {assignError}
                </div>
              )}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setAssignOpen(false)}
                  className="border-ink-200 text-ink-700 min-h-[2.75rem] rounded-xl border px-4 text-sm font-semibold active:scale-[.98]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!assignExerciseId || assignExercise.isPending}
                  className="bg-primary-500 font-display hover:bg-primary-600 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  {assignExercise.isPending ? 'Assignation…' : 'Assigner'}
                </button>
              </div>
            </form>
          )}
          {activeAssignments.length === 0 ? (
            !assignOpen && <EmptyCard>Aucun devoir en cours</EmptyCard>
          ) : (
            <div className="border-ink-200 ocean:bg-white/5 overflow-hidden rounded-2xl border bg-white">
              {activeAssignments.map((a, i) => {
                const meta = ASSIGN_STATUS_META[a.status as keyof typeof ASSIGN_STATUS_META];
                const Icon = meta.icon;
                const late = isOverdue(a, nowMs);
                return (
                  <div
                    key={a.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3',
                      i < activeAssignments.length - 1 && 'border-ink-100 border-b',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
                        late ? 'bg-error-50' : meta.bg,
                      )}
                    >
                      {late ? (
                        <AlertTriangle className="text-error-500 h-4 w-4" />
                      ) : (
                        <Icon className={cn('h-4 w-4', meta.color)} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-ink-900 truncate text-sm font-bold">
                        {a.exercise_title}
                      </p>
                      <p
                        className={cn(
                          'mt-0.5 font-sans text-xs',
                          late ? 'text-error-600 font-semibold' : 'text-ink-500',
                        )}
                      >
                        {late ? 'En retard' : meta.label}
                        {a.due_at &&
                          ` · pour le ${new Date(a.due_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`}
                        {a.note && ` · ${a.note}`}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (window.confirm(`Supprimer le devoir « ${a.exercise_title} » ?`))
                          deleteAssignment.mutate({ assignmentId: a.id, studentId });
                      }}
                      disabled={deleteAssignment.isPending}
                      className="text-ink-300 hover:text-error-500 hover:bg-error-50 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors"
                      aria-label={`Supprimer le devoir ${a.exercise_title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {data.completed_assignments.length > 0 && (
            <CollapsiblePanel
              className="mt-3"
              title={`Devoirs terminés (${data.completed_assignments.length})`}
            >
              <ul className="space-y-2">
                {data.completed_assignments.map((a) => (
                  <li key={a.id} className="flex items-center gap-3">
                    <CheckCircle2 className="text-xp-500 h-4 w-4 shrink-0" aria-hidden />
                    <span className="text-ink-800 min-w-0 flex-1 truncate font-sans text-sm">
                      {a.exercise_title}
                    </span>
                    <span className="text-ink-400 shrink-0 font-sans text-xs">
                      {a.completed_at ? relativeTime(a.completed_at) : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </CollapsiblePanel>
          )}
        </section>
      )}

      {/* ── Progression : chiffres, tendance, derniers exercices ───────────────── */}
      {isActive && (
        <section className="mb-6">
          <SectionTitle>Progression</SectionTitle>
          {data.stats.total_attempts > 0 ? (
            <p className="text-ink-600 mb-3 font-sans text-sm">
              <span className="text-ink-900 font-semibold">{data.stats.completed_attempts}</span>{' '}
              exercice{data.stats.completed_attempts > 1 ? 's' : ''} terminé
              {data.stats.completed_attempts > 1 ? 's' : ''} sur {data.stats.total_attempts}{' '}
              commencé
              {data.stats.total_attempts > 1 ? 's' : ''} (20 derniers)
            </p>
          ) : (
            <EmptyCard>{firstName} n’a encore commencé aucun exercice.</EmptyCard>
          )}
          <StudentProgressBlock progress={exerciseProgress} />
          {data.recent_attempts.length > 0 && (
            <div className="border-ink-200 ocean:bg-white/5 overflow-hidden rounded-2xl border bg-white">
              {data.recent_attempts.map((a, i) => {
                const meta = ATTEMPT_META[a.status as keyof typeof ATTEMPT_META];
                const Icon = meta.icon;
                const inner = (
                  <>
                    <div
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
                        meta.bg,
                      )}
                    >
                      <Icon className={cn('h-4 w-4', meta.color)} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-ink-900 truncate text-sm font-bold">
                        {a.exercise_title}
                      </p>
                      <p className="text-ink-500 mt-0.5 font-sans text-xs">
                        {meta.label} · {relativeTime(a.started_at)}
                      </p>
                    </div>
                  </>
                );
                const rowCls = cn(
                  'flex min-h-[2.75rem] items-center gap-3 px-4 py-3',
                  i < data.recent_attempts.length - 1 && 'border-ink-100 border-b',
                );
                return a.status === 'completed' ? (
                  <Link
                    key={a.attempt_id}
                    to="/attempts/$attemptId"
                    params={{ attemptId: a.attempt_id }}
                    className={cn(rowCls, 'hover:bg-ink-50 active:scale-[0.99]')}
                  >
                    {inner}
                    <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" aria-hidden />
                  </Link>
                ) : (
                  <div key={a.attempt_id} className={rowCls}>
                    {inner}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── SKILLCARDS de l'élève + ajout « à travailler » (migration 0277) ───────── */}
      {isActive && (
        <div id="skillcards" className="scroll-mt-4">
          <TutorStudentSkillcards
            studentId={studentId}
            studentName={data.display_name ?? 'Cet élève'}
            tutorId={tutorId}
          />
        </div>
      )}

      {/* ── Notes et évals saisies par l'élève, partagées avec son prof (0322) ── */}
      {isActive && (
        <TutorStudentEvaluations
          studentId={studentId}
          studentName={data.display_name ?? 'Cet élève'}
        />
      )}

      {/* ── Rendez-vous : à venir puis passés (notes / résumé) ───────────────────── */}
      {isActive && (upcoming.length > 0 || pastBookings.length > 0) && (
        <section className="mb-6">
          {upcoming.length > 0 && (
            <>
              <SectionTitle>Rendez-vous à venir</SectionTitle>
              <ul className="border-ink-200 ocean:bg-white/5 divide-ink-100 mb-4 divide-y overflow-hidden rounded-2xl border bg-white">
                {upcoming.map((r) => (
                  <li key={r.reservationId}>
                    <Link
                      to="/rendez-vous/$reservationId"
                      params={{ reservationId: r.reservationId }}
                      className="hover:bg-ink-50 flex min-h-[2.75rem] items-center gap-3 px-4 py-3"
                    >
                      <CalendarClock className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="text-ink-900 block font-sans text-sm font-semibold first-letter:uppercase">
                          {dayLabel(r.startsAt, nowMs)} · {timeLabel(r.startsAt)}
                        </span>
                        <span className="text-ink-500 block font-sans text-xs">
                          {RENDEZ_VOUS_KIND_LABELS[r.kind]}
                          {r.status === 'pending_payment' && ' · en attente de paiement'}
                        </span>
                      </span>
                      <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {pastBookings.length > 0 && (
            <>
              <SectionTitle>Rendez-vous passés</SectionTitle>
              <div className="space-y-2">
                {pastBookings.map((booking) => (
                  <PastRendezVousCard
                    key={booking.reservationId}
                    item={{
                      reservationId: booking.reservationId,
                      otherPartyName: data.display_name,
                      startsAt: booking.startsAt,
                      kind: booking.kind,
                      status: booking.status,
                      archivedByMe: booking.archivedByMe,
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Discussions avec cet élève (migrations 0265-0267) ───────────────────── */}
      {isActive && (
        <section className="mb-6">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SectionTitle>Discussions</SectionTitle>
            <button
              type="button"
              onClick={() => void handleNewDiscussion()}
              disabled={createDiscussion.isPending}
              className="text-primary-600 hover:bg-primary-50 -mt-3 flex min-h-[2.75rem] items-center gap-1.5 rounded-xl px-2 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-50"
            >
              <MessageCirclePlus className="h-4 w-4" aria-hidden />
              Nouvelle discussion
            </button>
          </div>
          {discussionError && studentDiscussions.length > 0 && (
            <p role="alert" className="text-accent-700 mb-2 text-xs font-medium">
              {discussionError}
            </p>
          )}
          <DiscussionsList
            discussions={studentDiscussions}
            myRole="tutor"
            emptyLabel={`Aucune discussion avec ${data.display_name} pour l’instant.`}
            onOpen={(id) =>
              void navigate({ to: '/discussions/$discussionId', params: { discussionId: id } })
            }
          />
        </section>
      )}

      {/* ── Fichiers partagés avec cet élève (migration 0261) — dans les deux sens ── */}
      {isActive && (
        <section className="mb-6">
          <SectionTitle>Fichiers</SectionTitle>
          <DocumentsPanel
            target={{ kind: 'contact', peerId: studentId }}
            canUpload
            emptyLabel={`Aucun fichier partagé avec ${data.display_name}.`}
          />
        </section>
      )}

      {/* ── Gestion du suivi (rare) : alternance, retrait ───────────────────────── */}
      {isActive && (
        <section className="border-ink-100 mt-8 border-t pt-4" aria-label="Gérer le suivi">
          {!data.is_alternant && (
            <BecomeAlternantAction
              tutorId={tutorId}
              studentId={studentId}
              studentName={data.display_name}
            />
          )}
          {link && (
            <button
              type="button"
              onClick={() => void handleRevoke()}
              disabled={revokeStudent.isPending}
              className="text-ink-500 hover:text-error-600 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium underline disabled:opacity-50"
            >
              <UserMinus className="h-4 w-4" aria-hidden />
              Retirer {firstName} de mes élèves
            </button>
          )}
        </section>
      )}

      {/* Pending — nothing to show yet */}
      {isPending && (
        <div className="bg-ink-50 border-ink-200 rounded-2xl border p-8 text-center">
          <Clock className="text-ink-300 mx-auto mb-3 h-8 w-8" />
          <p className="font-display text-ink-700 text-sm font-bold">En attente de réponse</p>
          <p className="text-ink-500 mx-auto mt-1 max-w-xs font-sans text-sm">
            Les résultats et les devoirs seront disponibles dès que l'élève acceptera l'invitation.
          </p>
        </div>
      )}

      {/* Aucun lien individuel (ex. élève vu via une classe) ou lien retiré */}
      {(data.link_status === 'none' || data.link_status === 'revoked') && (
        <div className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 rounded-2xl border p-5">
          <p className="font-display text-ink-900 text-sm font-bold">
            {data.link_status === 'revoked'
              ? `${firstName} n’est plus dans tes élèves`
              : `${firstName} n’est pas encore relié à toi individuellement`}
          </p>
          <p className="text-ink-600 mt-1 font-sans text-sm">
            Pour voir sa progression, lui donner des devoirs ou programmer un rendez-vous, invite-le
            : il acceptera depuis son application.
          </p>
          <Button
            className="mt-3 w-full sm:w-auto"
            loading={inviteStudent.isPending}
            disabled={inviteStudent.isSuccess}
            onClick={() => inviteStudent.mutate({ tutorId, studentId, subjects: [] })}
          >
            <UserPlus className="h-4 w-4" />
            {inviteStudent.isSuccess ? 'Invitation envoyée' : `Inviter ${firstName}`}
          </Button>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/tutor/contacts"
      className="font-display text-ink-500 hover:text-ink-900 -ml-1 mb-4 inline-flex min-h-[2.75rem] items-center gap-1.5 px-1 text-sm font-semibold transition-colors"
    >
      <ArrowLeft className="h-4 w-4" />
      Mon réseau
    </Link>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Video;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-ink-200 text-ink-800 hover:border-primary-400 ocean:bg-white/5 flex min-h-[2.75rem] flex-col items-center justify-center gap-0.5 rounded-xl border bg-white px-1 py-2 font-sans text-sm font-semibold active:scale-[0.98] sm:flex-row sm:gap-1.5"
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {label}
    </button>
  );
}
